import { type MouseEventHandler, PropsWithChildren } from 'react'
import { Style, styled } from 'inlines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { type IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { Text } from '../typography/Text'
import {
  Size,
  linkStyle,
  linkSizeStyle,
  linkIconSizeStyle,
} from './linkStyleHeper'
import { LinkAnchor } from './LinkAnchor'

interface TextLinkBaseProps extends PropsWithChildren {
  disabled?: boolean
  icon?: IconDefinition
  size?: Size
  isBold?: boolean
  hasIcon?: boolean
  iconPosition?: 'left' | 'right'
  noHover?: boolean
  onMouseDown?: MouseEventHandler<HTMLElement>
  target?: string
  textStyle?: Style
  containerStyle?: Style
}

type TextLinkWithToProps = TextLinkBaseProps & {
  to: string
  onClick?: MouseEventHandler<HTMLElement>
}

type TextLinkWithOnClickProps = TextLinkBaseProps & {
  to?: never
  onClick: MouseEventHandler<HTMLElement>
}

export type TextLinkProps = TextLinkWithToProps | TextLinkWithOnClickProps

const buttonResetStyle: Style = {
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  padding: 0,
}

export const TextLink = ({
  children,
  disabled = false,
  icon,
  to,
  onClick,
  size = 'medium',
  isBold = false,
  hasIcon = false,
  iconPosition = 'right',
  noHover = false,
  onMouseDown,
  target,
  textStyle,
  containerStyle,
}: TextLinkProps) => {
  const text = (
    <Text.Medium
      key="text"
      style={{
        fontWeight: isBold ? 600 : 500,
        ...linkSizeStyle[size],
        ...textStyle,
      }}
    >
      {children}
    </Text.Medium>
  )
  const content = [text]

  const resolvedIcon = icon
    ? {
        icon,
        style: linkIconSizeStyle[size],
      }
    : hasIcon
      ? {
          icon: iconPosition === 'right' ? faArrowRight : faArrowLeft,
          style: linkIconSizeStyle[size],
        }
      : undefined

  if (resolvedIcon) {
    const iconNode = (
      <FontAwesomeIcon
        key="icon"
        icon={resolvedIcon.icon}
        style={{ ...resolvedIcon.style }}
      />
    )

    if (iconPosition === 'right') {
      content.push(iconNode)
    } else {
      content.unshift(iconNode)
    }
  }

  const style = { ...linkStyle }
  if (noHover) {
    style['&:hover'] = { opacity: '0.65' }
  }

  const rootStyle = { ...style, ...containerStyle }

  if (to !== undefined) {
    return (
      <LinkAnchor
        disabled={disabled}
        onClick={onClick}
        onMouseDown={
          onMouseDown
        }
        style={rootStyle}
        target={target}
        to={to}
      >
        {content}
      </LinkAnchor>
    )
  }

  return (
    <styled.button
      disabled={disabled}
      onClick={onClick}
      onMouseDown={onMouseDown}
      style={{
        ...buttonResetStyle,
        ...rootStyle,
      }}
      type="button"
    >
      {content}
    </styled.button>
  )
}
